import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import menuAdminService from '../services/menuAdminService';
import { Button } from '../components';

/**
 * Create/Edit Menu Page
 */
const CreateEditMenu = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    is_admin: false,
  });

  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Fetch menu data if editing
  useEffect(() => {
    if (isEdit) {
      fetchMenu();
    }
  }, [id]);

  const fetchMenu = async () => {
    try {
      setLoading(true);
      const response = await menuAdminService.getMenuById(id);
      if (response.success) {
        setFormData({
          name: response.data.name,
          code: response.data.code,
          is_admin: response.data.is_admin,
        });
        setError(null);
      }
    } catch (err) {
      console.error('Error fetching menu:', err);
      setError(err.message || 'Failed to fetch menu');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let finalValue = type === 'checkbox' ? checked : value;
    
    // Convert code to lowercase and replace spaces with underscores
    if (name === 'code') {
      finalValue = value.toLowerCase().replace(/\s+/g, '_');
    }
    
    setFormData({
      ...formData,
      [name]: finalValue,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      setError('Menu name is required');
      return;
    }

    if (!formData.code.trim()) {
      setError('Menu code is required');
      return;
    }

    // Validate code format (only lowercase letters, numbers, and underscores)
    const codeRegex = /^[a-z0-9_]+$/;
    if (!codeRegex.test(formData.code)) {
      setError('Menu code can only contain lowercase letters, numbers, and underscores');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Ensure code is lowercase before submitting
      const submitData = {
        ...formData,
        code: formData.code.toLowerCase(),
      };

      let response;
      if (isEdit) {
        response = await menuAdminService.updateMenu(id, submitData);
      } else {
        response = await menuAdminService.createMenu(submitData);
      }

      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/menus');
        }, 1500);
      } else {
        setError(response.message || 'Failed to save menu');
      }
    } catch (err) {
      console.error('Error saving menu:', err);
      setError(err.message || 'Failed to save menu');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/menus')}
          className="p-2 hover:bg-gray-200 rounded-lg transition"
        >
          <ArrowLeft className="w-6 h-6 text-gray-600" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEdit ? 'Edit Menu' : 'Create Menu'}
          </h1>
          <p className="text-gray-500 mt-2">
            {isEdit ? 'Update menu details' : 'Create a new system menu'}
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow p-6 max-w-2xl">
        {/* Success Message */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex gap-3">
            <AlertCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-green-900">Success</h3>
              <p className="text-green-700">
                Menu {isEdit ? 'updated' : 'created'} successfully!
              </p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-red-900">Error</h3>
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name Field */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Menu Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Dashboard, Reports, Settings"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={submitting}
            />
            <p className="text-sm text-gray-500 mt-1">The display name of the menu</p>
          </div>

          {/* Code Field */}
          <div>
            <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
              Menu Code *
            </label>
            <input
              type="text"
              id="code"
              name="code"
              value={formData.code}
              onChange={handleChange}
              placeholder="e.g., dashboard, reports, settings"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 lowercase"
              disabled={isEdit || submitting}
            />
            <p className="text-sm text-gray-500 mt-1">
              {isEdit 
                ? 'Unique identifier for this menu (cannot be changed after creation)'
                : 'Unique identifier (lowercase letters, numbers, underscores only)'
              }
            </p>
          </div>

          {/* Admin Only Checkbox */}
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="is_admin"
              name="is_admin"
              checked={formData.is_admin}
              onChange={handleChange}
              className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              disabled={submitting}
            />
            <div className="flex-1">
              <label htmlFor="is_admin" className="block text-sm font-medium text-gray-700">
                Admin Only Menu
              </label>
              <p className="text-sm text-gray-500 mt-1">
                Check this if the menu should only be accessible to admin users. Regular managers and users will not see this menu.
              </p>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-blue-900 mb-2">Menu Code Guidelines:</h4>
            <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
              <li>Use lowercase letters only</li>
              <li>Use underscores (_) instead of spaces</li>
              <li>No special characters</li>
              <li>Example: "family_portal", "duty_roster", "to_do_list"</li>
            </ul>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-4">
            <Button
              type="submit"
              variant="primary"
              loading={submitting}
              className="flex-1"
            >
              {submitting ? 'Saving...' : (isEdit ? 'Update Menu' : 'Create Menu')}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/menus')}
              disabled={submitting}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateEditMenu;
